// Ported from vlookup-dramatic/components/ui/sheet.tsx without Base UI.
// A controlled side drawer: <Sheet open onOpenChange><SheetTrigger/><SheetContent side="right">…</SheetContent></Sheet>
import React, { createContext, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { getOverlayRoot } from "../../lib/overlay-root";
import { XIcon } from "../../lib/icons";
import { Button } from "./button";

const Ctx = createContext({ open: false, setOpen: () => {} });

export function Sheet({ open = false, onOpenChange = () => {}, children }) {
  return <Ctx.Provider value={{ open, setOpen: onOpenChange }}>{children}</Ctx.Provider>;
}

export function SheetTrigger({ className, children, ...props }) {
  const { open, setOpen } = useContext(Ctx);
  return (
    <button
      type="button"
      data-slot="sheet-trigger"
      aria-expanded={open}
      className={className}
      onClick={() => setOpen(true)}
      {...props}
    >
      {children}
    </button>
  );
}

export function SheetClose({ className, children, ...props }) {
  const { setOpen } = useContext(Ctx);
  return (
    <button type="button" data-slot="sheet-close" className={className} onClick={() => setOpen(false)} {...props}>
      {children}
    </button>
  );
}

const SIDE = {
  right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
  left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
  top: "inset-x-0 top-0 h-auto border-b",
  bottom: "inset-x-0 bottom-0 h-auto border-t",
};

export function SheetContent({ className, children, side = "right", showCloseButton = true, ...props }) {
  const { open, setOpen } = useContext(Ctx);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, setOpen]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        data-slot="sheet-overlay"
        className="fixed inset-0 z-50 bg-black/10 backdrop-blur-xs"
        onClick={() => setOpen(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "fixed z-50 flex flex-col gap-4 bg-popover bg-clip-padding text-sm text-popover-foreground shadow-lg",
          SIDE[side] || SIDE.right,
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <Button variant="ghost" size="icon-sm" className="absolute top-3 right-3" onClick={() => setOpen(false)}>
            <XIcon />
            <span className="sr-only">Close</span>
          </Button>
        )}
      </div>
    </>,
    getOverlayRoot()
  );
}

export function SheetHeader({ className, ...props }) {
  return <div data-slot="sheet-header" className={cn("flex flex-col gap-0.5 p-4", className)} {...props} />;
}

export function SheetFooter({ className, ...props }) {
  return <div data-slot="sheet-footer" className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />;
}

export function SheetTitle({ className, ...props }) {
  return <div data-slot="sheet-title" className={cn("font-heading text-base font-medium text-foreground", className)} {...props} />;
}

export function SheetDescription({ className, ...props }) {
  return <p data-slot="sheet-description" className={cn("text-sm text-muted-foreground", className)} {...props} />;
}
